import React from 'react';

import { Flex, Spin, type TableColumnsType, Tooltip } from 'antd-v5';
import cn from 'classnames';
import { FaThList } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';
import { IconButton } from 'scouting-ui/lib/Button';
import MemberTypeTag from 'scouting-ui/lib/Tags/MemberTypeTag';

import { RegistrationSituationStatus, RenewalStatus } from '@appTypes/enums';
import type { SubUnitType } from '@appTypes/esb';
import { NO_SUB_UNIT_KEY } from '@modules/roster-v2/models/groupedRoster';
import type { DedupedPosition } from '@modules/roster-v2/models/rosterMember';
import { MemberKind } from '@modules/roster-v2/models/rosterMember';
import {
  type RosterMemberView,
  subUnitKey,
} from '@modules/roster-v2/models/rosterMemberView';
import { formatToUIDate } from '@modules/utils/datesLuxon';
import MultiValueLabel from '@shared/components/MultiValueLabel';
import PersonAvatar from '@shared/components/PersonAvatar/PersonAvatar';
import { intl } from '@shared/localization';

import styles from './NewRosterTable.module.scss';

export interface GroupByToggleProps {
  subUnitType: SubUnitType;
  grouped: boolean;
  onChange: (grouped: boolean) => void;
}

// The header cell sorts on click and on Enter/Space
const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

function GroupByToggle(props: GroupByToggleProps) {
  const { subUnitType, grouped, onChange } = props;
  const label = intl.formatMessage({
    id: `rosterV2.table.groupBy.${subUnitType}`,
  });

  return (
    <Tooltip title={label}>
      <IconButton
        color="white"
        variant="link"
        size="small"
        className={cn(styles.groupByToggle, {
          [styles.groupByActive]: grouped,
        })}
        icon={<FaThList className={styles.groupByIcon} />}
        aria-label={label}
        aria-pressed={grouped}
        onClick={e => {
          e.stopPropagation();
          onChange(!grouped);
        }}
        onKeyDown={stopPropagation}
      />
    </Tooltip>
  );
}

interface FilterOption {
  text: string;
  value: string;
}

export interface SubUnitLoadingState {
  adults: boolean;
  youths: boolean;
}

/** Its filter picks groups while grouped */
export const SUB_UNIT_COLUMN = 'subUnits';

/** Registration Status filter value for members with no status */
export const UNKNOWN_REGISTRATION_STATUS = 'unknownRegistration';

/** Maps the registration situation token to a human-readable label. */
function registrationStatusLabel(status?: RegistrationSituationStatus): string {
  switch (status) {
    case RegistrationSituationStatus.Active:
      return intl.formatMessage({ id: 'rosterV2.table.regSituation.active' });
    case RegistrationSituationStatus.Lapsed:
      return intl.formatMessage({ id: 'rosterV2.table.regSituation.lapsed' });
    case RegistrationSituationStatus.Expired:
      return intl.formatMessage({ id: 'rosterV2.table.regSituation.expired' });
    default:
      return '—';
  }
}

export interface GetColumnsOptions {
  positionFilters: FilterOption[];
  subUnitFilters: FilterOption[];
  isCubScout: boolean;
  subUnitLoading: SubUnitLoadingState;
  isMobile: boolean;
  /** Adds an Unknown Registration Status option, selected by default */
  hasUnknownRegistration: boolean;
  groupBy?: GroupByToggleProps;
}

export function getColumns(
  options: GetColumnsOptions,
): TableColumnsType<RosterMemberView> {
  const {
    positionFilters,
    subUnitFilters,
    isCubScout,
    subUnitLoading,
    isMobile,
    hasUnknownRegistration,
    groupBy,
  } = options;
  const subUnitTitle = intl.formatMessage({ id: 'rosterV2.table.subUnit' });
  // Only when some member has it: antd keeps a selected value with no option,
  // so unticking every option would still filter by it
  const unknownRegistration: string[] = hasUnknownRegistration
    ? [UNKNOWN_REGISTRATION_STATUS]
    : [];

  return [
    {
      title: intl.formatMessage({ id: 'shared.name' }),
      key: 'name',
      width: isMobile ? 120 : 220,
      minWidth: isMobile ? 100 : 160,
      fixed: 'left',
      render: (_: unknown, record) => (
        <Flex gap="small" align="center">
          <PersonAvatar
            size="md"
            isAdult={record.isAdult}
            gender={record.gender}
            pictureUrl={record.pictureUrl ?? undefined}
          />
          {record.preferredName}
        </Flex>
      ),
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      defaultSortOrder: 'ascend',
    },
    {
      title: intl.formatMessage({ id: 'shared.memberId' }),
      dataIndex: 'memberId',
      key: 'memberId',
      width: 120,
      minWidth: 110,
      sorter: (a, b) =>
        a.memberId.localeCompare(b.memberId, undefined, { numeric: true }),
    },
    {
      title: intl.formatMessage({ id: 'shared.type' }),
      dataIndex: 'kind',
      key: 'kind',
      width: '15ch',
      minWidth: 40,
      filters: [
        {
          text: intl.formatMessage({ id: 'shared.youth' }),
          value: MemberKind.Youth,
        },
        {
          text: intl.formatMessage({ id: 'shared.adult' }),
          value: MemberKind.Adult,
        },
        {
          text: intl.formatMessage({ id: 'rosterV2.table.participant' }),
          value: MemberKind.Participant,
        },
      ],
      onFilter: (value, record) => record.kind === value,
      sorter: (a, b) => a.kind.localeCompare(b.kind),
      render: (value: MemberKind) => <MemberTypeTag memberType={value} />,
    },
    {
      title: intl.formatMessage({ id: 'rosterV2.table.age' }),
      key: 'age',
      width: 80,
      minWidth: 60,
      render: (_: unknown, record) => record.displayAge,
      sorter: (a, b) =>
        (a.age ?? Number.NEGATIVE_INFINITY) -
        (b.age ?? Number.NEGATIVE_INFINITY),
    },
    {
      title: intl.formatMessage({ id: 'rosterV2.table.program' }),
      dataIndex: 'programs',
      key: 'programs',
      width: 120,
      hidden: !isCubScout,
      render: (values: string[]) => (values.length ? values.join(', ') : '—'),
    },
    {
      title: groupBy ? (
        <Flex component="span" align="center" gap={4}>
          {subUnitTitle}
          <GroupByToggle {...groupBy} />
        </Flex>
      ) : (
        subUnitTitle
      ),
      // The toggle has its own tooltip; keep the sort one on the arrows
      showSorterTooltip: groupBy ? { target: 'sorter-icon' } : undefined,
      dataIndex: 'subUnitNames',
      key: SUB_UNIT_COLUMN,
      width: 180,
      minWidth: 140,
      // Values are group keys (`den:12`, `patrol:7`) or `NO_SUB_UNIT_KEY`
      filters: subUnitFilters,
      filterSearch: true,
      onFilter: (value, record) =>
        value === NO_SUB_UNIT_KEY
          ? record.subUnits.length === 0
          : record.subUnits.some(ref => subUnitKey(ref) === value),
      sorter: (a, b) => a.displaySubUnits.localeCompare(b.displaySubUnits),
      render: (values: string[], record) => {
        const isLoading =
          record.kind === MemberKind.Adult
            ? subUnitLoading.adults
            : subUnitLoading.youths;
        if (isLoading && !record.subUnit) return <Spin size="small" />;
        return <MultiValueLabel values={values} />;
      },
    },
    {
      title: intl.formatMessage({ id: 'shared.position' }),
      dataIndex: 'positions',
      key: 'positions',
      width: 280,
      filters: positionFilters,
      filterSearch: true,
      onFilter: (value, record) =>
        record.positions.some(p => p.position === String(value)),
      sorter: (a, b) =>
        (a.positions[0]?.position ?? '').localeCompare(
          b.positions[0]?.position ?? '',
        ),
      render: (values: DedupedPosition[]) => (
        <MultiValueLabel values={values.map(p => p.position)} />
      ),
    },
    {
      title: intl.formatMessage({ id: 'rosterV2.table.renewalStatus' }),
      key: 'renewalStatus',
      width: 180,
      filters: Object.values(RenewalStatus).map(value => ({
        text: value,
        value,
      })),
      onFilter: (value, record) => record.renewalStatus === value,
      render: (_: unknown, record) => {
        const status = record.renewalStatus;
        if (!status) return '—';
        if (!record.isOverYouthAge) return status;
        const overAge = intl.formatMessage(
          { id: 'rosterV2.table.overAge' },
          { maxAge: record.maxYouthAge },
        );
        return `${status} ${overAge}`;
      },
      sorter: (a, b) =>
        (a.renewalStatus ?? '').localeCompare(b.renewalStatus ?? ''),
    },
    {
      title: intl.formatMessage({ id: 'rosterV2.table.expirationDate' }),
      key: 'registrationExpireDt',
      dataIndex: 'registrationExpireDt',
      width: 160,
      render: (value: string | undefined, record) => {
        if (record.registration?.isYearlyMembershipRenewalPaid)
          return (
            <FormattedMessage id="advancement.PackRoster.column.expirationDate.future" />
          );
        if (!value) return 'N/A';
        return formatToUIDate(value);
      },
      sorter: (a, b) =>
        (a.registrationExpireDt ?? '').localeCompare(
          b.registrationExpireDt ?? '',
        ),
    },
    {
      title: intl.formatMessage({ id: 'rosterV2.table.registrationStatus' }),
      key: 'registrationStatus',
      width: 180,
      filters: [
        ...Object.values(RegistrationSituationStatus).map(value => ({
          text: registrationStatusLabel(value),
          value,
        })),
        ...unknownRegistration.map(value => ({
          text: intl.formatMessage({
            id: 'rosterV2.table.regSituation.unknown',
          }),
          value,
        })),
      ],
      // Expired members are hidden until the filter includes them
      defaultFilteredValue: [
        RegistrationSituationStatus.Active,
        RegistrationSituationStatus.Lapsed,
        ...unknownRegistration,
      ],
      filterResetToDefaultFilteredValue: true,
      onFilter: (value, record) =>
        (record.registrationStatus ?? UNKNOWN_REGISTRATION_STATUS) === value,
      render: (_: unknown, record) =>
        registrationStatusLabel(record.registrationStatus),
      sorter: (a, b) =>
        registrationStatusLabel(a.registrationStatus).localeCompare(
          registrationStatusLabel(b.registrationStatus),
        ),
    },
  ];
}
